import { ArrowDownRight, ArrowUpRight, Info, Minus } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatPct } from '@/lib/format';
import { cn } from '@/lib/cn';

interface GrowthBadgeProps {
  value: number | null | undefined;
  unavailableLabel: string;
  description: string;
}

export function GrowthBadge({ value, unavailableLabel, description }: GrowthBadgeProps) {
  const { t } = useI18n();
  const known = value !== null && value !== undefined && Number.isFinite(value);
  const Icon = known && value > 0 ? ArrowUpRight : known && value < 0 ? ArrowDownRight : Minus;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex min-h-9 max-w-full flex-wrap items-center gap-1.5 rounded-lg text-left text-[10px] font-medium text-text-3 [@media(pointer:coarse)]:min-h-11"
          title={t('Comprendre la variation sur 7 jours', 'Understand the 7-day change')}
        >
          {known ? (
            <>
              <span
                className={cn(
                  'inline-flex items-center gap-0.5 rounded-md px-1.5 py-1',
                  value > 0
                    ? 'bg-emerald-soft text-emerald'
                    : value < 0
                      ? 'bg-rose-soft text-rose'
                      : 'bg-bg-2 text-text-3',
                )}
              >
                <Icon className="size-3" aria-hidden="true" />
                {formatPct(value, { digits: 1 })}
              </span>
              <span>{t('sur 7 j', 'over 7 d')}</span>
            </>
          ) : (
            <span>{unavailableLabel}</span>
          )}
          <Info className="size-3 shrink-0" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        aria-label={t('Comparaison sur 7 jours', '7-day comparison')}
        align="start"
        sideOffset={8}
        className="w-64 max-w-[calc(100vw-2rem)] rounded-xl p-3 text-[12px] leading-relaxed text-text-2 motion-reduce:animate-none"
      >
        <p className="mb-1.5 font-semibold text-text-1">
          {t('Comparaison sur 7 jours', '7-day comparison')}
        </p>
        <p>{description}</p>
      </PopoverContent>
    </Popover>
  );
}
